import {
	createAuthController,
	createProfileController,
	createRequestFn,
	createSignupController,
} from '@saas/api-client';
import { useMutation } from '@tanstack/react-query';
import { globalAuthRequestFunction, publicRequestFunction } from '../http/request';
import { makeMutation, makeQuery } from '../http/queryUtils';

// Public auth endpoints (login, register, invite) — no token required
const publicAuthController = createAuthController(publicRequestFunction);

// Pending signup (ADR-0046). Anonymous up to `complete`; `complete-social` runs on the session a
// social tap already minted, which is why it is the one call on the authenticated controller.
const publicSignupController = createSignupController(publicRequestFunction);
const authedSignupController = createSignupController(globalAuthRequestFunction);

// --- Mutations ---
// register creates the account (emailVerified:false) and triggers the first
// email-verification OTP. It stays alive for installed mobile builds; the web signup
// proves the mailbox first through the signup endpoints below.
const useSignup = makeMutation({ mutationFunction: publicAuthController.register });

const useSignupForInvitedUser = makeMutation({ mutationFunction: publicAuthController.registerByCompanyID });

// --- Queries ---

const authServiceKeys = {
	base: 'AUTH',
	invitePreview: (params: Parameters<typeof publicAuthController.previewInvite>[0]) =>
		[authServiceKeys.base, 'INVITE_PREVIEW', params] as const,
	newMemberInvitePreview: (params: Parameters<typeof publicAuthController.previewNewMemberInvite>[0]) =>
		[authServiceKeys.base, 'NEW_MEMBER_INVITE_PREVIEW', params] as const,
};

/** The workspace and inviter behind an existing user's invitation link, read before accepting it. */
const useGetInvitePreview = makeQuery({
	queryFunction: publicAuthController.previewInvite,
	queryKeyFactory: (params) => [...authServiceKeys.invitePreview(params)],
});

/** The same read for an invitee without an account, whose link names the address and workspace. */
const useGetNewMemberInvitePreview = makeQuery({
	queryFunction: publicAuthController.previewNewMemberInvite,
	queryKeyFactory: (params) => [...authServiceKeys.newMemberInvitePreview(params)],
});

/** Step one: ask for a six-digit code, or be told the address already has an account. */
const useStartSignup = makeMutation({ mutationFunction: publicSignupController.start });

/** A second code for the same address, behind the resend cooldown. */
const useResendSignupCode = makeMutation({ mutationFunction: publicSignupController.resend });

/** Step two: exchange the code for a single-use proof token. */
const useVerifySignupCode = makeMutation({ mutationFunction: publicSignupController.verify });

/** Step three, email path: the proof token plus the details create the User. */
const useCompleteSignup = makeMutation({ mutationFunction: publicSignupController.complete });

/** Step three, social path: the User already exists and incomplete; this finishes it. */
const useCompleteSocialSignup = makeMutation({ mutationFunction: authedSignupController.completeSocial });

/** Proof before a link: send a code to an existing account that never proved its mailbox. */
const useStartLinkProof = makeMutation({ mutationFunction: publicSignupController.linkProofStart });

/** Proof before a link: the code that writes `mailboxProvenAt` and unblocks the exchange. */
const useVerifyLinkProof = makeMutation({ mutationFunction: publicSignupController.linkProofVerify });

export const useGetProfile = () => {
	return useMutation({
		mutationFn: async (tokens: { token: string }) => {
			const requestFn = createRequestFn({
				baseUrl: import.meta.env.VITE_API_URL,
				async onRequest(_url, init) {
					const headers = new Headers(init.headers);
					headers.set('Authorization', `Bearer ${tokens.token}`);
					return { ...init, headers };
				},
			});
			const profileController = createProfileController(requestFn);
			const result = await profileController.getProfile();
			if (result.error) throw new Error(result.error.message);
			return result.data;
		},
	});
};

const useAcceptInvitation = makeMutation({ mutationFunction: publicAuthController.acceptInvite });

// --- Export ---
export const authService = {
	useSignup,
	useGetProfile,
	useAcceptInvitation,
	useSignupForInvitedUser,
	useStartSignup,
	useResendSignupCode,
	useVerifySignupCode,
	useCompleteSignup,
	useCompleteSocialSignup,
	useGetInvitePreview,
	useGetNewMemberInvitePreview,
	useStartLinkProof,
	useVerifyLinkProof,
};
